/**
 * Enhanced Translation Table Component with Client-Side Processing
 * 
 * Provides paginated, filterable table view for translation data with 
 * language column selection and search functionality.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  FileText,
  AlertCircle,
  Loader,
  Hash,
  Languages,
  Search,
  Eye
} from 'lucide-react'
import PaginationControls from './PaginationControls'
import LanguageColumnSelector from './LanguageSelector'
import SearchFilter from './SearchFilter'

const TranslationTable = ({
  data = [],
  headers = [],
  pagination,
  languageInfo,
  searchValue = '',
  isLoading = false,
  error = null,
  onPageChange,
  onItemsPerPageChange,
  onLanguageToggle,
  onShowAllLanguages,
  onHideAllLanguages,
  onResetLanguages,
  onSearchChange,
  onClearSearch,
  onExportFiltered,
  className = ''
}) => {
  const { t } = useTranslation()

  // Loading state
  if (isLoading) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-3">
            <Loader className="w-6 h-6 animate-spin text-blue-500" />
            <span className="text-gray-600 dark:text-gray-400">
              {t('review.table.loading')}
            </span>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center space-y-3 text-center">
            <AlertCircle className="w-12 h-12 text-red-500" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                {t('review.table.error.title')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {error.message || t('review.table.error.generic')}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center space-y-3 text-center">
            {searchValue ? (
              <>
                <Search className="w-12 h-12 text-gray-400" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                    {t('review.table.noResults.title')}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t('review.table.noResults.description', { query: searchValue })}
                  </p>
                </div>
              </>
            ) : (
              <>
                <FileText className="w-12 h-12 text-gray-400" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                    {t('review.table.empty.title')}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t('review.table.empty.description')}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Helper to detect if column is a language column
  const isLanguageColumn = (header) => {
    if (!languageInfo) return false
    return languageInfo.allLanguages.some(lang => 
      header === lang || header.includes(lang)
    )
  }

  // Helper to get column type badge
  const getColumnTypeBadge = (header) => {
    if (!languageInfo) return null
    
    if (header === languageInfo.sourceLanguage) {
      return (
        <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1 py-0.5 rounded">
          {t('review.table.source')}
        </span>
      )
    }
    
    if (languageInfo.targetLanguages.includes(header)) {
      return (
        <span className="ml-2 text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-1 py-0.5 rounded">
          {t('review.table.target')}
        </span>
      )
    }
    
    return null
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Table Controls */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        {/* Search Filter */}
        <SearchFilter
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          onClearSearch={onClearSearch}
          onExportFiltered={onExportFiltered}
          resultCount={pagination?.totalFiltered}
          totalCount={pagination?.totalRows}
          isExportEnabled={true}
          className="flex-1"
        />

        {/* Language Column Selector */}
        <LanguageColumnSelector
          languageInfo={languageInfo}
          onLanguageToggle={onLanguageToggle}
          onShowAll={onShowAllLanguages}
          onHideAll={onHideAllLanguages}
          onResetToDefault={onResetLanguages}
        />
      </div>

      {/* Table Summary */}
      {pagination && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Hash className="w-4 h-4" />
              <span>{t('review.table.totalRows')}: {pagination.totalRows.toLocaleString()}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Languages className="w-4 h-4" />
              <span>{t('review.table.visibleColumns')}: {headers.length}</span>
            </div>
            {pagination.totalFiltered !== pagination.totalRows && (
              <div className="flex items-center space-x-1">
                <Search className="w-4 h-4" />
                <span>{t('review.table.filteredRows')}: {pagination.totalFiltered.toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Responsive Table Container */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            {/* Table Header */}
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                {/* Row number column */}
                <th className="sticky left-0 z-10 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-1">
                    <Hash className="w-3 h-3" />
                    <span>{t('review.table.row')}</span>
                  </div>
                </th>
                
                {/* Data columns */}
                {headers.map((header) => (
                  <th
                    key={header}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    <div className="flex items-center">
                      {isLanguageColumn(header) && (
                        <Languages className="w-3 h-3 mr-1" />
                      )}
                      <span className="truncate max-w-[120px]" title={header}>
                        {header}
                      </span>
                      {getColumnTypeBadge(header)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {data.map((row, index) => {
                const rowNumber = pagination ? 
                  ((pagination.currentPage - 1) * pagination.itemsPerPage) + index + 1 : 
                  index + 1

                return (
                  <tr 
                    key={row._rowIndex || index} 
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    {/* Row number column */}
                    <td className="sticky left-0 z-10 px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 text-xs">
                        {rowNumber}
                      </div>
                    </td>

                    {/* Data columns */}
                    {headers.map((header) => (
                      <td
                        key={header}
                        className="px-4 py-4 text-sm text-gray-900 dark:text-white"
                      >
                        <div 
                          className="max-w-xs truncate" 
                          title={row[header]}
                        >
                          <div className="whitespace-pre-wrap break-words">
                            {row[header] || (
                              <span className="text-gray-400 italic">
                                {t('review.table.emptyCell')}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {pagination && (
        <div className="pt-4">
          <PaginationControls
            pagination={pagination}
            onPageChange={onPageChange}
            onItemsPerPageChange={onItemsPerPageChange}
          />
        </div>
      )}

    </div>
  )
}

export default TranslationTable